/**
 * MyShop Dashboard Screen
 * SUPER_ADMIN only dashboard to manage all shops
 * Features:
 * - Shop statistics and graphs
 * - List all shops
 * - Delete shops
 * - Toggle shop online/offline status
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Dimensions,
  Modal,
  TextInput,
  useWindowDimensions,
  Image,
} from 'react-native';
import { useNavigate } from 'react-router-dom';
import {
  useAuthTokens,
  shopManagementService,
  userManagementService,
  subscriptionService,
  reportsService,
  getCustomerShopMenuUrl,
  type Shop,
  type ShopUser,
  type ResetPasswordRequest,
  type SubscriptionPlan,
  type ShopSubscription,
  type CreateShopSubscriptionRequest,
  type ShopReportsResponse
} from '@app/core';
import { Button, Card, Text } from '@app/design-system';
import { colors } from '@app/design-system';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface MyShopDashboardProps {
  onLogout?: () => void;
}

interface ShopStats {
  totalShops: number;
  liveShops: number;
  offlineShops: number;
  totalCatalogs: number;
  shopsWithCatalogs: number;
}

export const MyShopDashboard: React.FC<MyShopDashboardProps> = ({ onLogout }) => {
  const navigate = useNavigate();
  const { width } = useWindowDimensions();
  const isDesktop = width > 768; // Tablet/Desktop breakpoint

  const [showMenu, setShowMenu] = useState(false);

  const [shops, setShops] = useState<Shop[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<ShopStats>({
    totalShops: 0,
    liveShops: 0,
    offlineShops: 0,
    totalCatalogs: 0,
    shopsWithCatalogs: 0,
  });
  const [updatingShop, setUpdatingShop] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedShopType, setSelectedShopType] = useState<string>('ALL');
  const [selectedStatus, setSelectedStatus] = useState<string>('ALL'); // 'ALL', 'ONLINE', 'OFFLINE'

  // User management state
  const [shopUsers, setShopUsers] = useState<{ [shopId: string]: ShopUser[] }>({});
  const [loadingUsers, setLoadingUsers] = useState<{ [shopId: string]: boolean }>({});
  const [expandedShops, setExpandedShops] = useState<{ [shopId: string]: boolean }>({});
  const [resetPasswordModal, setResetPasswordModal] = useState<{
    visible: boolean;
    userId: string;
    userName: string;
  }>({ visible: false, userId: '', userName: '' });
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [processingAction, setProcessingAction] = useState<string | null>(null);

  // QR Code Modal State
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedShopForQR, setSelectedShopForQR] = useState<Shop | null>(null);

  // Shop Subscription State
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [shopSubscriptions, setShopSubscriptions] = useState<{ [shopId: string]: ShopSubscription[] }>({});
  const [loadingShopSubscriptions, setLoadingShopSubscriptions] = useState<{ [shopId: string]: boolean }>({});
  const [showAssignSubscriptionModal, setShowAssignSubscriptionModal] = useState(false);

  // Executive Reports State
  const [showReports, setShowReports] = useState(false);
  const [reportsData, setReportsData] = useState<ShopReportsResponse | null>(null);
  const [loadingReports, setLoadingReports] = useState(false);

  const [assigningToShop, setAssigningToShop] = useState<Shop | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [subscriptionStartDate, setSubscriptionStartDate] = useState('');
  const [subscriptionEndDate, setSubscriptionEndDate] = useState('');
  const [autoRenew, setAutoRenew] = useState(true);
  const [subscriptionPartnerId, setSubscriptionPartnerId] = useState(''); // Partner ID for subscription
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [planForm, setPlanForm] = useState<{
    planName?: string;
    subscriptionDays?: number;
    startDate?: string;
    endDate?: string;
    isActive?: boolean;
  }>({});


  const tokens = useAuthTokens();

  useEffect(() => {
    loadShops();
    loadSubscriptionPlans();
  }, []);

  const loadShops = async () => {
    try {
      setLoading(true);
      console.log('📊 [Dashboard] Loading all shops...');

      const allShops = await shopManagementService.getAllShops();
      console.log('✅ [Dashboard] Loaded shops:', allShops.length);

      setShops(allShops);
      calculateStats(allShops);

      // Load subscriptions for all shops
      console.log('📋 [Dashboard] Loading subscriptions for all shops...');
      allShops.forEach(shop => {
        loadShopSubscriptions(shop.id);
      });
    } catch (error) {
      console.error('❌ [Dashboard] Failed to load shops:', error);
      // Error logged - user can retry manually
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (shopsList: Shop[]) => {
    const stats: ShopStats = {
      totalShops: shopsList.length,
      liveShops: shopsList.filter((s) => s.isLive === true).length,
      offlineShops: shopsList.filter((s) => s.isLive === false).length,
      totalCatalogs: 0, // TODO: Fetch from API if available
      shopsWithCatalogs: 0, // TODO: Fetch from API if available
    };

    console.log('📊 [Dashboard] Stats calculated:', stats);
    setStats(stats);
  };

  const handleDeleteShop = async (shopId: string, shopName: string) => {
    console.log('🗑️ [Dashboard] Delete shop requested:', { shopId, shopName });

    // Browser confirmation dialog
    const confirmed =
      typeof window !== 'undefined' && window.confirm
        ? window.confirm(
            `⚠️ WARNING: This will permanently delete "${shopName}" and ALL associated data:\n\n` +
              `• All catalog items\n` +
              `• All users (ADMIN and STAFF)\n` +
              `• All QR codes\n` +
              `• Shop configuration\n\n` +
              `This action CANNOT be undone!\n\n` +
              `Are you absolutely sure you want to delete this shop?`
          )
        : false;

    if (!confirmed) {
      console.log('🚫 [Dashboard] Shop deletion cancelled');
      return;
    }

    try {
      setUpdatingShop(shopId);
      console.log('🗑️ [Dashboard] Deleting shop:', shopId);

      await shopManagementService.deleteShop(shopId);

      console.log('✅ [Dashboard] Shop deleted successfully');

      // Remove from local state
      const updatedShops = shops.filter((s) => s.id !== shopId);
      setShops(updatedShops);
      calculateStats(updatedShops);
    } catch (error) {
      console.error('❌ [Dashboard] Failed to delete shop:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete shop';
      console.error('❌ [Dashboard] Error message:', errorMessage);
    } finally {
      setUpdatingShop(null);
    }
  };

  const handleToggleShopStatus = async (
    shopId: string,
    shopName: string,
    currentStatus: boolean | null | undefined
  ) => {
    const newStatus = !currentStatus;
    console.log('🔄 [Dashboard] Toggle shop status:', {
      shopId,
      shopName,
      currentStatus,
      newStatus,
    });

    const confirmed =
      typeof window !== 'undefined' && window.confirm
        ? window.confirm(
            `${newStatus ? '🟢 Set Online' : '🔴 Set Offline'}\n\n` +
              `Shop: ${shopName}\n` +
              `Status will change to: ${newStatus ? 'LIVE (Online)' : 'OFFLINE'}\n\n` +
              `Continue?`
          )
        : false;

    if (!confirmed) {
      console.log('🚫 [Dashboard] Status toggle cancelled');
      return;
    }

    try {
      setUpdatingShop(shopId);
      console.log('🔄 [Dashboard] Updating shop status...');

      await shopManagementService.updateShopStatus(shopId, newStatus);

      console.log('✅ [Dashboard] Shop status updated successfully');

      // Update local state
      const updatedShops = shops.map((shop) =>
        shop.id === shopId ? { ...shop, isLive: newStatus } : shop
      );
      setShops(updatedShops);
      calculateStats(updatedShops);
    } catch (error) {
      console.error('❌ [Dashboard] Failed to update shop status:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to update shop status';
      console.error('❌ [Dashboard] Error message:', errorMessage);
    } finally {
      setUpdatingShop(null);
    }
  };

  /**
   * Open shop's customer-facing menu in new tab
   */
  const handleOpenShopMenu = (shop: Shop) => {
    if (typeof window !== 'undefined') {
      let customerViewUrl: string;

      // Use shopUrl if present, otherwise fall back to qrCodeUrl, or default menu URL
      if (shop.shopUrl) {
        // If shopUrl doesn't start with http, add https://
        customerViewUrl = shop.shopUrl.startsWith('http')
          ? shop.shopUrl
          : `https://${shop.shopUrl}`;
        console.log('🌐 [Dashboard] Using custom shop URL:', { shopName: shop.name, shopUrl: shop.shopUrl, url: customerViewUrl });
      } else if (shop.qrCodeUrl) {
        customerViewUrl = shop.qrCodeUrl;
        console.log('🌐 [Dashboard] Using QR code URL:', { shopName: shop.name, url: customerViewUrl });
      } else {
        // Fallback to default customer menu URL
        customerViewUrl = getCustomerShopMenuUrl(shop.id!);
        console.log('🌐 [Dashboard] Using default menu URL:', { shopName: shop.name, url: customerViewUrl });
      }

      window.open(customerViewUrl, '_blank');
    }
  };

  /**
   * Open QR code modal for a shop
   */
  const handleShowQRCode = (shop: Shop) => {
    setSelectedShopForQR(shop);
    setShowQRModal(true);
    console.log('📱 [Dashboard] Opening QR code for shop:', shop.name);
  };

  /**
   * Close QR code modal
   */
  const handleCloseQRModal = () => {
    setShowQRModal(false);
    setSelectedShopForQR(null);
  };

  /**
   * Download shop QR code in printable format
   * Generates a high-quality printable QR with shop branding
   */
  const handleDownloadQRCode = async () => {
    if (!selectedShopForQR?.qrCode) {
      console.warn('⚠️ [Dashboard] No QR code available to download');
      return;
    }

    try {
      console.log('📥 [Dashboard] Generating printable QR code for:', selectedShopForQR.name);

      if (typeof window === 'undefined') {
        console.warn('⚠️ [Dashboard] Window not available');
        return;
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        console.error('❌ [Dashboard] Could not create canvas context');
        return;
      }

      // Set canvas dimensions - Portrait format (700:1000) like payment QR codes
      const canvasWidth = 700;
      const canvasHeight = 1000;
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      // Load images
      const qrImg = document.createElement('img') as HTMLImageElement;
      const bgImg = selectedShopForQR.shopImage ? document.createElement('img') as HTMLImageElement : null;

      const imagePromise = new Promise<void>((resolve, reject) => {
        let qrLoaded = false;
        let bgLoaded = !bgImg;

        const checkAllLoaded = () => {
          if (qrLoaded && bgLoaded) {
            try {
              console.log('🎨 [Dashboard] Drawing printable QR code...');

              // Draw background
              if (bgImg && selectedShopForQR.shopImage) {
                // Draw shop image as background
                const bgAspect = bgImg.width / bgImg.height;
                const canvasAspect = canvasWidth / canvasHeight;

                let drawWidth, drawHeight, offsetX, offsetY;

                if (bgAspect > canvasAspect) {
                  drawHeight = canvasHeight;
                  drawWidth = bgImg.width * (canvasHeight / bgImg.height);
                  offsetX = (canvasWidth - drawWidth) / 2;
                  offsetY = 0;
                } else {
                  drawWidth = canvasWidth;
                  drawHeight = bgImg.height * (canvasWidth / bgImg.width);
                  offsetX = 0;
                  offsetY = (canvasHeight - drawHeight) / 2;
                }

                ctx.drawImage(bgImg, offsetX, offsetY, drawWidth, drawHeight);

                // 70% white overlay (30% shop image transparency)
                ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
                ctx.fillRect(0, 0, canvasWidth, canvasHeight);
              } else {
                // White background if no shop image
                ctx.fillStyle = '#FFFFFF';
                ctx.fillRect(0, 0, canvasWidth, canvasHeight);
              }

              let currentY = 50;

              // Draw shop name on top - larger font and bold
              const shopName = selectedShopForQR.name || 'Shop';
              ctx.fillStyle = '#1a1a1a';
              ctx.font = 'bold 48px Arial, sans-serif';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'top';

              // Word wrapping for shop name
              const maxNameWidth = canvasWidth - 80;
              const nameWords = shopName.split(' ');
              let nameLines: string[] = [];
              let nameLine = '';

              nameWords.forEach((word) => {
                const testLine = nameLine + word + ' ';
                const metrics = ctx.measureText(testLine);
                if (metrics.width > maxNameWidth && nameLine) {
                  nameLines.push(nameLine.trim());
                  nameLine = word + ' ';
                } else {
                  nameLine = testLine;
                }
              });
              if (nameLine.trim()) {
                nameLines.push(nameLine.trim());
              }

              // Limit to 2 lines
              if (nameLines.length > 2) {
                nameLines = nameLines.slice(0, 2);
                nameLines[1] = nameLines[1].slice(0, -3) + '...';
              }

              // Draw shop name lines
              nameLines.forEach((line) => {
                ctx.fillText(line, canvasWidth / 2, currentY);
                currentY += 60;
              });

              currentY += 20; // Extra spacing after shop name

              // Draw instructional text
              ctx.fillStyle = '#333333';
              ctx.font = 'bold 28px Arial, sans-serif';

              const instruction1 = 'Find Our Products and Prices';
              const instruction2 = 'by Scanning Below QR';

              ctx.fillText(instruction1, canvasWidth / 2, currentY);
              currentY += 38;
              ctx.fillText(instruction2, canvasWidth / 2, currentY);
              currentY += 60; // Two line spacing (40px + extra)

              // Draw QR code in center with white background box
              const qrSize = 400;
              const qrX = (canvasWidth - qrSize) / 2;
              const qrY = currentY;
              const qrPadding = 20;

              // White box with border
              ctx.fillStyle = '#FFFFFF';
              ctx.fillRect(qrX - qrPadding, qrY - qrPadding, qrSize + (qrPadding * 2), qrSize + (qrPadding * 2));
              ctx.strokeStyle = '#000000';
              ctx.lineWidth = 4;
              ctx.strokeRect(qrX - qrPadding, qrY - qrPadding, qrSize + (qrPadding * 2), qrSize + (qrPadding * 2));

              // Draw QR code
              ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

              // Draw footer
              const footerY = canvasHeight - 60;
              ctx.fillStyle = '#666666';
              ctx.font = '20px Arial, sans-serif';
              ctx.fillText('Powered by Bynaryx Labs™', canvasWidth / 2, footerY);

              // Convert to blob and download
              canvas.toBlob((blob) => {
                if (blob) {
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `${shopName.replace(/[^a-z0-9]/gi, '_')}_Printable_QR.png`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                  console.log('✅ [Dashboard] Printable QR downloaded:', link.download);
                }
              }, 'image/png', 1.0);

              resolve();
            } catch (drawError) {
              console.error('❌ [Dashboard] Error drawing QR:', drawError);
              reject(drawError);
            }
          }
        };

        qrImg.onload = () => {
          console.log('✅ [Dashboard] QR code image loaded');
          qrLoaded = true;
          checkAllLoaded();
        };

        qrImg.onerror = () => {
          console.error('❌ [Dashboard] Failed to load QR code image');
          reject(new Error('Failed to load QR code image'));
        };

        if (bgImg && selectedShopForQR.shopImage) {
          console.log('📥 [Dashboard] Loading shop background image');
          bgImg.onload = () => {
            console.log('✅ [Dashboard] Shop background image loaded');
            bgLoaded = true;
            checkAllLoaded();
          };

          bgImg.onerror = () => {
            console.warn('⚠️ [Dashboard] Failed to load shop background image, using white background');
            bgLoaded = true;
            checkAllLoaded();
          };

          bgImg.crossOrigin = 'anonymous';
          bgImg.src = selectedShopForQR.shopImage;
        }

        qrImg.crossOrigin = 'anonymous';
        qrImg.src = selectedShopForQR.qrCode;
      });

      await imagePromise;
    } catch (error) {
      console.error('❌ [Dashboard] Failed to download QR code:', error);
      if (typeof window !== 'undefined' && window.alert) {
        window.alert('❌ Failed to download QR code. Please try again.');
      }
    }
  };

  // User Management Functions
  const loadShopUsers = async (shopId: string) => {
    try {
      setLoadingUsers(prev => ({ ...prev, [shopId]: true }));
      console.log('👥 [Dashboard] Loading users for shop:', shopId);

      const users = await userManagementService.getShopUsers(shopId);
      console.log('✅ [Dashboard] Loaded users:', users.length);

      setShopUsers(prev => ({ ...prev, [shopId]: users }));
    } catch (error) {
      console.error('❌ [Dashboard] Failed to load shop users:', error);
    } finally {
      setLoadingUsers(prev => ({ ...prev, [shopId]: false }));
    }
  };

  const toggleShopExpansion = async (shopId: string) => {
    const isCurrentlyExpanded = expandedShops[shopId];

    setExpandedShops(prev => ({
      ...prev,
      [shopId]: !isCurrentlyExpanded
    }));

    // Load users when expanding for the first time
    if (!isCurrentlyExpanded && !shopUsers[shopId]) {
      await loadShopUsers(shopId);
    }
  };

  const handleDeleteUser = async (userId: string, userName: string, shopId: string) => {
    console.log('🗑️ [Dashboard] Delete user requested:', { userId, userName });

    const confirmed =
      typeof window !== 'undefined' && window.confirm
        ? window.confirm(
            `⚠️ WARNING: Delete User "${userName}"?\n\n` +
              `This will permanently delete this user account.\n` +
              `This action CANNOT be undone!\n\n` +
              `Are you sure you want to delete this user?`
          )
        : false;

    if (!confirmed) {
      console.log('🚫 [Dashboard] User deletion cancelled');
      return;
    }

    try {
      setProcessingAction(userId);
      console.log('🔄 [Dashboard] Deleting user...');

      await userManagementService.deleteUser(userId);
      console.log('✅ [Dashboard] User deleted successfully');

      // Reload users for this shop
      await loadShopUsers(shopId);
    } catch (error) {
      console.error('❌ [Dashboard] Failed to delete user:', error);
    } finally {
      setProcessingAction(null);
    }
  };

  const openResetPasswordModal = (userId: string, userName: string) => {
    setResetPasswordModal({
      visible: true,
      userId,
      userName
    });
    setNewPassword('');
    setConfirmPassword('');
  };

  const closeResetPasswordModal = () => {
    setResetPasswordModal({ visible: false, userId: '', userName: '' });
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleResetPassword = async () => {
    if (!newPassword.trim()) {
      console.error('❌ [Dashboard] New password is required');
      return;
    }

    if (newPassword !== confirmPassword) {
      console.error('❌ [Dashboard] Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      console.error('❌ [Dashboard] Password must be at least 8 characters');
      return;
    }

    try {
      setProcessingAction(resetPasswordModal.userId);
      console.log('🔄 [Dashboard] Resetting user password...');

      const request: ResetPasswordRequest = {
        newPassword,
        confirmPassword
      };

      await userManagementService.resetUserPassword(resetPasswordModal.userId, request);
      console.log('✅ [Dashboard] User password reset successfully');

      closeResetPasswordModal();
    } catch (error) {
      console.error('❌ [Dashboard] Failed to reset user password:', error);
    } finally {
      setProcessingAction(null);
    }
  };

  // ==================== Subscription Management Functions ====================

  const loadSubscriptionPlans = async () => {
    try {
      setLoadingPlans(true);
      console.log('📋 [Dashboard] Loading subscription plans...');

      const plans = await subscriptionService.getSubscriptionPlans();
      console.log('✅ [Dashboard] Loaded subscription plans:', plans.length);

      setSubscriptionPlans(plans);
    } catch (error) {
      console.error('❌ [Dashboard] Failed to load subscription plans:', error);
    } finally {
      setLoadingPlans(false);
    }
  };

  const loadReportsData = async () => {
    try {
      setLoadingReports(true);
      console.log('📊 [Dashboard] Loading executive reports...');
      const reports = await reportsService.getShopReports();
      console.log('✅ [Dashboard] Reports loaded successfully:', reports);
      setReportsData(reports);
    } catch (error) {
      console.error('❌ [Dashboard] Failed to load reports:', error);
      setReportsData(null);
    } finally {
      setLoadingReports(false);
    }
  };

  const handleToggleReports = () => {
    const newShowReports = !showReports;
    setShowReports(newShowReports);

    // Lazy load reports data only when showing for the first time
    if (newShowReports && !reportsData && !loadingReports) {
      loadReportsData();
    }
  };

  const openPlanModal = (plan?: SubscriptionPlan) => {
    if (plan) {
      setEditingPlan(plan);
      setPlanForm({
        planName: plan.planName,
        subscriptionDays: plan.subscriptionDays,
        startDate: plan.startDate || new Date().toISOString().slice(0, 16) + ':00',
        endDate: plan.endDate || '2099-12-31T23:59:59',
        isActive: plan.isActive !== undefined ? plan.isActive : true,
      });
    } else {
      setEditingPlan(null);
      setPlanForm({
        planName: '',
        subscriptionDays: 30,
        startDate: new Date().toISOString().slice(0, 16) + ':00',
        endDate: '2099-12-31T23:59:59',
        isActive: true,
      });
    }
    setShowPlanModal(true);
  };

  const closePlanModal = () => {
    setShowPlanModal(false);
    setEditingPlan(null);
    setPlanForm({
      planName: '',
      subscriptionDays: 30,
      startDate: new Date().toISOString().slice(0, 16) + ':00',
      endDate: '2099-12-31T23:59:59',
      isActive: true,
    });
  };

  const handleSavePlan = async () => {
    // Validation
    if (!planForm.planName?.trim()) {
      alert('⚠️ Plan name is required');
      return;
    }

    if (!planForm.subscriptionDays || planForm.subscriptionDays < 1) {
      alert('⚠️ Subscription days must be at least 1');
      return;
    }

    try {
      setProcessingAction('saving-plan');
      console.log('💾 [Dashboard] Saving subscription plan...', planForm);

      const planData: SubscriptionPlan = {
        planName: planForm.planName!.trim(),
        subscriptionDays: planForm.subscriptionDays!,
        startDate: planForm.startDate,
        endDate: planForm.endDate,
        isActive: planForm.isActive !== undefined ? planForm.isActive : true,
      };

      if (editingPlan) {
        await subscriptionService.updateSubscriptionPlan(editingPlan.id!, planData);
        console.log('✅ [Dashboard] Plan updated successfully');
        alert('✅ Subscription plan updated successfully!');
      } else {
        await subscriptionService.createSubscriptionPlan(planData);
        console.log('✅ [Dashboard] Plan created successfully');
        alert('✅ Subscription plan created successfully!');
      }

      await loadSubscriptionPlans();
      closePlanModal();
    } catch (error: any) {
      console.error('❌ [Dashboard] Failed to save plan:', error);
      const errorMessage = error?.response?.data?.details || error?.response?.data?.message || error?.message || 'Unknown error';
      alert(`❌ Failed to save plan: ${errorMessage}`);
    } finally {
      setProcessingAction(null);
    }
  };

  const handleDeletePlan = async (planId: string, planName: string) => {
    const confirmed =
      typeof window !== 'undefined' && window.confirm
        ? window.confirm(
            `⚠️ WARNING: Delete Subscription Plan "${planName}"?\n\n` +
              `This will permanently delete this plan.\n` +
              `Existing shop subscriptions will not be affected.\n\n` +
              `Are you sure?`
          )
        : false;

    if (!confirmed) {
      return;
    }

    try {
      setProcessingAction(planId);
      console.log('🗑️ [Dashboard] Deleting subscription plan...');

      await subscriptionService.deleteSubscriptionPlan(planId);
      console.log('✅ [Dashboard] Plan deleted successfully');

      await loadSubscriptionPlans();
    } catch (error) {
      console.error('❌ [Dashboard] Failed to delete plan:', error);
    } finally {
      setProcessingAction(null);
    }
  };

  // Shop Subscription Functions
  const loadShopSubscriptions = async (shopId: string) => {
    try {
      setLoadingShopSubscriptions(prev => ({ ...prev, [shopId]: true }));
      console.log('📋 [Dashboard] Loading subscriptions for shop:', shopId);

      const subscriptions = await subscriptionService.getShopSubscriptions(shopId);
      console.log('✅ [Dashboard] Loaded shop subscriptions:', subscriptions?.length);
      if (subscriptions && subscriptions.length > 0) {
        console.log('🔍 [Dashboard] First subscription details:', subscriptions[0]);
      }

      // Ensure subscriptions is always an array
      const subscriptionsArray = Array.isArray(subscriptions) ? subscriptions : [];
      setShopSubscriptions(prev => ({ ...prev, [shopId]: subscriptionsArray }));
    } catch (error) {
      console.error('❌ [Dashboard] Failed to load shop subscriptions:', error);
      // Set empty array on error
      setShopSubscriptions(prev => ({ ...prev, [shopId]: [] }));
    } finally {
      setLoadingShopSubscriptions(prev => ({ ...prev, [shopId]: false }));
    }
  };

  const openAssignSubscriptionModal = (shop: Shop) => {
    setAssigningToShop(shop);
    setSelectedPlanId('');
    const today = new Date().toISOString().split('T')[0];
    const oneMonthLater = new Date();
    oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);
    setSubscriptionStartDate(today);
    setSubscriptionEndDate(oneMonthLater.toISOString().split('T')[0]);
    setAutoRenew(true);
    setShowAssignSubscriptionModal(true);
  };

  const closeAssignSubscriptionModal = () => {
    setShowAssignSubscriptionModal(false);
    setAssigningToShop(null);
    setSelectedPlanId('');
    setSubscriptionStartDate('');
    setSubscriptionEndDate('');
    setAutoRenew(true);
    setSubscriptionPartnerId(''); // Reset partner ID
  };

  const handleAssignSubscription = async () => {
    if (!selectedPlanId) {
      console.error('❌ [Dashboard] Please select a subscription plan');
      return;
    }

    if (!subscriptionStartDate || !subscriptionEndDate) {
      console.error('❌ [Dashboard] Start and end dates are required');
      return;
    }

    if (new Date(subscriptionEndDate) <= new Date(subscriptionStartDate)) {
      console.error('❌ [Dashboard] End date must be after start date');
      return;
    }

    try {
      setProcessingAction('assigning-subscription');
      console.log('💾 [Dashboard] Assigning subscription to shop...');

      // Convert dates from YYYY-MM-DD to YYYY-MM-DDTHH:mm:ss format
      const startDateTime = subscriptionStartDate.includes('T')
        ? subscriptionStartDate
        : `${subscriptionStartDate}T00:00:00`;
      const endDateTime = subscriptionEndDate.includes('T')
        ? subscriptionEndDate
        : `${subscriptionEndDate}T23:59:59`;

      const request: CreateShopSubscriptionRequest = {
        shopId: assigningToShop!.id,
        planId: selectedPlanId,
        planStartDate: startDateTime,
        planEndDate: endDateTime,
        autoRenew,
        ...(subscriptionPartnerId && subscriptionPartnerId.trim() && { partnerId: subscriptionPartnerId.trim() }), // Include partnerId if provided
      };

      console.log('📅 [Dashboard] Sending subscription request:', request);

      await subscriptionService.createShopSubscription(request);
      console.log('✅ [Dashboard] Subscription assigned successfully');

      await loadShopSubscriptions(assigningToShop!.id);
      closeAssignSubscriptionModal();
    } catch (error) {
      console.error('❌ [Dashboard] Failed to assign subscription:', error);
    } finally {
      setProcessingAction(null);
    }
  };

  const handleDeleteShopSubscription = async (subscriptionId: string, shopId: string) => {
    const confirmed =
      typeof window !== 'undefined' && window.confirm
        ? window.confirm(
            `⚠️ WARNING: Remove this subscription?\n\n` +
              `This will remove the subscription from the shop.\n\n` +
              `Are you sure?`
          )
        : false;

    if (!confirmed) {
      return;
    }

    try {
      setProcessingAction(subscriptionId);
      console.log('🗑️ [Dashboard] Deleting shop subscription...');

      await subscriptionService.deleteShopSubscription(subscriptionId);
      console.log('✅ [Dashboard] Shop subscription deleted successfully');

      await loadShopSubscriptions(shopId);
    } catch (error) {
      console.error('❌ [Dashboard] Failed to delete shop subscription:', error);
    } finally {
      setProcessingAction(null);
    }
  };

  const handleRenewShopSubscription = async (shopId: string, planId: string, subscriptionId: string) => {
    const confirmed =
      typeof window !== 'undefined' && window.confirm
        ? window.confirm(
            `🔄 Renew Subscription?\n\n` +
              `This will renew the subscription with the same plan.\n\n` +
              `Are you sure?`
          )
        : false;

    if (!confirmed) {
      return;
    }

    try {
      setProcessingAction(subscriptionId);
      console.log('🔄 [Dashboard] Renewing shop subscription...', { shopId, planId });

      await subscriptionService.renewShopSubscription(shopId, planId);
      console.log('✅ [Dashboard] Shop subscription renewed successfully');

      // Reload subscriptions to show the updated data
      await loadShopSubscriptions(shopId);

      // Show success message
      if (typeof window !== 'undefined' && window.alert) {
        window.alert('✅ Subscription renewed successfully!');
      }
    } catch (error) {
      console.error('❌ [Dashboard] Failed to renew shop subscription:', error);
      if (typeof window !== 'undefined' && window.alert) {
        window.alert('❌ Failed to renew subscription. Please try again.');
      }
    } finally {
      setProcessingAction(null);
    }
  };

  // Navigation handlers
  const handleNavigateTo = (path: string) => {
    console.log(`🎯 [MyShopDashboard] Navigating to: ${path}`);
    setShowMenu(false);
    navigate(path);
  };

  const handleMenuLogout = () => {
    setShowMenu(false);
    if (onLogout) {
      onLogout();
    }
  };

  // Render screen based on navigation

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text variant="body" color="secondary" style={styles.loadingText}>
          Loading dashboard...
        </Text>
      </View>
    );
  }

  return (
    <>
      {/* Header with Menu */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <Text variant="h2" bold style={styles.headerTitle}>
            👑 MyShop Dashboard
          </Text>
          <Text variant="caption" color="secondary" style={styles.headerSubtitle}>
            SUPER ADMIN Panel
          </Text>
        </View>
        <TouchableOpacity
          onPress={() => setShowMenu(true)}
          style={styles.menuButton}
          testID="menu-button"
        >
          <Text style={styles.menuIcon}>☰</Text>
          <Text variant="caption" style={styles.menuText}>
            Menu
          </Text>
        </TouchableOpacity>
      </View>

      {/* Menu Modal */}
      <Modal
        visible={showMenu}
        animationType="fade"
        transparent={true}
        onRequestClose={() => setShowMenu(false)}
      >
        <TouchableOpacity
          style={styles.menuOverlay}
          activeOpacity={1}
          onPress={() => setShowMenu(false)}
        >
          <TouchableOpacity activeOpacity={1} onPress={(e) => e.stopPropagation()}>
            <View style={styles.menuContainer}>
              <View style={styles.menuHeader}>
                <Text variant="h3" bold>Menu</Text>
                <TouchableOpacity onPress={() => setShowMenu(false)}>
                  <Text style={styles.closeButton}>✕</Text>
                </TouchableOpacity>
              </View>

              <ScrollView style={styles.menuContent}>
                <TouchableOpacity
                  style={styles.menuItem}
                  onPress={() => handleNavigateTo('/admin/subscription-plans')}
                >
                  <Text style={styles.menuItemIcon}>💳</Text>
                  <Text style={styles.menuItemText}>Manage Subscription Plans</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.menuItem}
                  onPress={() => handleNavigateTo('/admin/partners')}
                >
                  <Text style={styles.menuItemIcon}>👥</Text>
                  <Text style={styles.menuItemText}>Manage Partners</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.menuItem}
                  onPress={() => handleNavigateTo('/admin/policies')}
                >
                  <Text style={styles.menuItemIcon}>📜</Text>
                  <Text style={styles.menuItemText}>Manage Policies</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.menuItem}
                  onPress={() => handleNavigateTo('/website-builder')}
                >
                  <Text style={styles.menuItemIcon}>🎨</Text>
                  <Text style={styles.menuItemText}>Website Builder</Text>
                </TouchableOpacity>

                <View style={styles.menuDivider} />

                <TouchableOpacity
                  style={styles.menuItem}
                  onPress={handleMenuLogout}
                >
                  <Text style={styles.menuItemIcon}>🚪</Text>
                  <Text style={styles.menuItemText}>Logout</Text>
                </TouchableOpacity>
              </ScrollView>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
        {/* Executive Reports Section */}
        <View style={styles.reportsSection}>
          <TouchableOpacity
            onPress={handleToggleReports}
            style={styles.reportsSectionHeader}
          >
            <Text variant="h3" bold style={styles.sectionTitle}>
              📊 Executive Reports
            </Text>
            <Text style={styles.toggleIcon}>
              {showReports ? '▼' : '▶'}
            </Text>
          </TouchableOpacity>

          {showReports && (
            <View style={styles.reportsContent}>
              {loadingReports ? (
                <View style={styles.reportsLoading}>
                  <ActivityIndicator size="large" color={colors.primary} />
                  <Text variant="body" color="secondary" style={styles.loadingText}>
                    Loading reports...
                  </Text>
                </View>
              ) : reportsData ? (
                <>
                  {/* Charts Grid */}
                  <View style={styles.chartsGrid}>
                    {/* Pie Chart 1: Shop Status (Live vs Offline) */}
                    <Card style={styles.chartCard}>
                      <Text variant="body" bold style={styles.chartTitle}>
                        Shop Status Distribution
                      </Text>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Live Shops', value: reportsData.liveShops, color: '#34C759' },
                              { name: 'Offline Shops', value: reportsData.offlineShops, color: '#FF3B30' }
                            ]}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, value, percent }) =>
                              `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                            }
                            outerRadius={60}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            <Cell fill="#34C759" />
                            <Cell fill="#FF3B30" />
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <View style={styles.chartLegend}>
                        <View style={styles.legendItem}>
                          <View style={[styles.legendColor, { backgroundColor: '#34C759' }]} />
                          <Text variant="caption">Live: {reportsData.liveShops}</Text>
                        </View>
                        <View style={styles.legendItem}>
                          <View style={[styles.legendColor, { backgroundColor: '#FF3B30' }]} />
                          <Text variant="caption">Offline: {reportsData.offlineShops}</Text>
                        </View>
                      </View>
                    </Card>

                    {/* Pie Chart 2: Subscription Status */}
                    <Card style={styles.chartCard}>
                      <Text variant="body" bold style={styles.chartTitle}>
                        Subscription Distribution
                      </Text>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'With Subscription', value: reportsData.shopsWithActiveSubscriptions, color: '#007AFF' },
                              { name: 'No Subscription', value: reportsData.shopsWithNoSubscriptions, color: '#FF9500' }
                            ]}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, value, percent }) =>
                              `${value} (${(percent * 100).toFixed(0)}%)`
                            }
                            outerRadius={60}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            <Cell fill="#007AFF" />
                            <Cell fill="#FF9500" />
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <View style={styles.chartLegend}>
                        <View style={styles.legendItem}>
                          <View style={[styles.legendColor, { backgroundColor: '#007AFF' }]} />
                          <Text variant="caption">Active: {reportsData.shopsWithActiveSubscriptions}</Text>
                        </View>
                        <View style={styles.legendItem}>
                          <View style={[styles.legendColor, { backgroundColor: '#FF9500' }]} />
                          <Text variant="caption">None: {reportsData.shopsWithNoSubscriptions}</Text>
                        </View>
                      </View>
                    </Card>

                    {/* Bar Chart: Catalogs per Shop */}
                    <Card style={[styles.chartCard, styles.chartCardWide]}>
                      <Text variant="body" bold style={styles.chartTitle}>
                        Catalogs per Shop
                      </Text>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart
                          data={reportsData.shops.slice(0, 10)}
                          margin={{ top: 10, right: 10, left: 0, bottom: 60 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="shopName"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            tick={{ fontSize: 10 }}
                          />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="totalCatalogs" fill="#007AFF" name="Catalogs" />
                        </BarChart>
                      </ResponsiveContainer>
                      <Text variant="caption" color="secondary" style={styles.chartNote}>
                        Showing top 10 shops by catalog count
                      </Text>
                    </Card>
                  </View>

                  {/* Summary Stats */}
                  <View style={styles.summaryStats}>
                    <Card style={styles.summaryCard}>
                      <Text variant="caption" color="secondary">Total Shops</Text>
                      <Text variant="h2" bold>{reportsData.totalShops}</Text>
                    </Card>
                    <Card style={styles.summaryCard}>
                      <Text variant="caption" color="secondary">Live Shops</Text>
                      <Text variant="h2" bold style={{ color: colors.success }}>{reportsData.liveShops}</Text>
                    </Card>
                    <Card style={styles.summaryCard}>
                      <Text variant="caption" color="secondary">Offline Shops</Text>
                      <Text variant="h2" bold style={{ color: colors.error }}>{reportsData.offlineShops}</Text>
                    </Card>
                  </View>
                </>
              ) : (
                <View style={styles.reportsError}>
                  <Text variant="body" color="secondary">
                    Failed to load reports. Please try again.
                  </Text>
                  <Button
                    title="Retry"
                    onPress={loadReportsData}
                    style={{ marginTop: 16 }}
                  />
                </View>
              )}
            </View>
          )}
        </View>


        {/* Shops List */}
        <View style={styles.shopsSection}>
          <Text variant="h3" bold style={styles.sectionTitle}>
            🏪 All Shops
          </Text>

          {/* Shop Type Filter */}
          {shops.length > 0 && (
            <View style={styles.shopTypeFilterContainer}>
              <Text variant="caption" color="secondary" style={styles.filterLabel}>
                Filter by Type:
              </Text>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                style={styles.shopTypeFilterScroll}
                contentContainerStyle={styles.shopTypeFilterContent}
              >
                <TouchableOpacity
                  style={[
                    styles.shopTypeFilterButton,
                    selectedShopType === 'ALL' && styles.shopTypeFilterButtonActive,
                  ]}
                  onPress={() => setSelectedShopType('ALL')}
                >
                  <Text
                    style={[
                      styles.shopTypeFilterText,
                      selectedShopType === 'ALL' && styles.shopTypeFilterTextActive,
                    ]}
                  >
                    All ({shops.length})
                  </Text>
                </TouchableOpacity>
                {/* Dynamic shop type buttons */}
                {Array.from(new Set(shops.map(shop => shop.shopType).filter(Boolean)))
                  .sort()
                  .map((shopType) => {
                    const count = shops.filter(s => s.shopType === shopType).length;
                    const displayName = shopType
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, (c: string) => c.toUpperCase());
                    return (
                      <TouchableOpacity
                        key={shopType}
                        style={[
                          styles.shopTypeFilterButton,
                          selectedShopType === shopType && styles.shopTypeFilterButtonActive,
                        ]}
                        onPress={() => setSelectedShopType(shopType)}
                      >
                        <Text
                          style={[
                            styles.shopTypeFilterText,
                            selectedShopType === shopType && styles.shopTypeFilterTextActive,
                          ]}
                        >
                          {displayName} ({count})
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
              </ScrollView>
            </View>
          )}

          {/* Status Filter (Online/Offline) */}
          {shops.length > 0 && (
            <View style={styles.statusFilterContainer}>
              <Text variant="caption" color="secondary" style={styles.filterLabel}>
                Filter by Status:
              </Text>
              <View style={styles.statusFilterButtons}>
                <TouchableOpacity
                  style={[
                    styles.statusFilterButton,
                    selectedStatus === 'ALL' && styles.statusFilterButtonActive,
                  ]}
                  onPress={() => setSelectedStatus('ALL')}
                >
                  <Text
                    style={[
                      styles.statusFilterText,
                      selectedStatus === 'ALL' && styles.statusFilterTextActive,
                    ]}
                  >
                    All ({shops.length})
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.statusFilterButton,
                    selectedStatus === 'ONLINE' && styles.statusFilterButtonActiveOnline,
                  ]}
                  onPress={() => setSelectedStatus('ONLINE')}
                >
                  <Text
                    style={[
                      styles.statusFilterText,
                      selectedStatus === 'ONLINE' && styles.statusFilterTextActiveOnline,
                    ]}
                  >
                    🟢 Online ({shops.filter(s => s.isLive === true).length})
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.statusFilterButton,
                    selectedStatus === 'OFFLINE' && styles.statusFilterButtonActiveOffline,
                  ]}
                  onPress={() => setSelectedStatus('OFFLINE')}
                >
                  <Text
                    style={[
                      styles.statusFilterText,
                      selectedStatus === 'OFFLINE' && styles.statusFilterTextActiveOffline,
                    ]}
                  >
                    🔴 Offline ({shops.filter(s => s.isLive === false).length})
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          )}

          {/* Search Field */}
          <View style={styles.searchContainer}>
            <TextInput
              style={styles.searchInput}
              placeholder="🔍 Search shops by name, owner, email, or mobile..."
              placeholderTextColor={colors.gray5}
              value={searchQuery}
              onChangeText={setSearchQuery}
              autoCapitalize="none"
              autoCorrect={false}
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity
                onPress={() => setSearchQuery('')}
                style={styles.clearSearchButton}
              >
                <Text style={styles.clearSearchText}>✕</Text>
              </TouchableOpacity>
            )}
          </View>

          {shops.length === 0 ? (
            <Card style={styles.emptyCard}>
              <Text style={styles.emptyIcon}>🏪</Text>
              <Text variant="body" bold style={styles.emptyTitle}>
                No Shops Found
              </Text>
              <Text variant="caption" color="secondary" style={styles.emptyText}>
                No shops have been created yet
              </Text>
            </Card>
          ) : (
            <View style={styles.shopsList}>
              {(() => {
                // Filter shops based on shop type filter, status filter, and search query
                const filteredShops = shops.filter((shop) => {
                  // First apply shop type filter
                  if (selectedShopType !== 'ALL' && shop.shopType !== selectedShopType) {
                    return false;
                  }

                  // Apply status filter (Online/Offline)
                  if (selectedStatus === 'ONLINE' && shop.isLive !== true) {
                    return false;
                  }
                  if (selectedStatus === 'OFFLINE' && shop.isLive !== false) {
                    return false;
                  }

                  // Then apply search query filter
                  if (!searchQuery.trim()) return true;

                  const query = searchQuery.toLowerCase().trim();
                  const searchableText = [
                    shop.name,
                    shop.owner,
                    shop.email,
                    shop.mobileNumber,
                    shop.shopType,
                    shop.address,
                  ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();

                  return searchableText.includes(query);
                });

                // Show no results message if search returns empty
                if (filteredShops.length === 0) {
                  let filterMessage = 'No shops found';
                  const filterParts: string[] = [];

                  if (selectedShopType !== 'ALL') {
                    filterParts.push(`type "${selectedShopType.replace(/_/g, ' ')}"`);
                  }
                  if (selectedStatus !== 'ALL') {
                    filterParts.push(`status "${selectedStatus}"`);
                  }
                  if (searchQuery) {
                    filterParts.push(`matching "${searchQuery}"`);
                  }

                  if (filterParts.length > 0) {
                    filterMessage = `No shops found with ${filterParts.join(', ')}`;
                  }

                  return (
                    <Card style={styles.emptyCard}>
                      <Text style={styles.emptyIcon}>🔍</Text>
                      <Text variant="body" bold style={styles.emptyTitle}>
                        No Shops Found
                      </Text>
                      <Text variant="caption" color="secondary" style={styles.emptyText}>
                        {filterMessage}
                      </Text>
                      {(selectedShopType !== 'ALL' || selectedStatus !== 'ALL' || searchQuery) && (
                        <TouchableOpacity
                          style={styles.clearFiltersButton}
                          onPress={() => {
                            setSelectedShopType('ALL');
                            setSelectedStatus('ALL');
                            setSearchQuery('');
                          }}
                        >
                          <Text style={styles.clearFiltersText}>Clear Filters</Text>
                        </TouchableOpacity>
                      )}
                    </Card>
                  );
                }

                return filteredShops.map((shop) => (
                <Card key={shop.id} style={styles.shopCard}>
                  {/* Shop Header */}
                  <View style={styles.shopHeader}>
                    <View style={styles.shopInfo}>
                      <View style={styles.shopTitleRow}>
                        <Text variant="body" bold style={styles.shopName}>
                          {shop.name}
                        </Text>

                        {/* Compact Action Buttons */}
                        <View style={styles.compactActionsRow}>
                          {/* Open Web Menu */}
                          <TouchableOpacity
                            style={styles.compactIconButton}
                            onPress={() => handleOpenShopMenu(shop)}
                            title="View Shop Menu"
                          >
                            <Text style={styles.compactIconEmoji}>🌐</Text>
                          </TouchableOpacity>

                          {/* Show QR Code */}
                          <TouchableOpacity
                            style={styles.compactIconButton}
                            onPress={() => handleShowQRCode(shop)}
                            title="View QR Code"
                          >
                            <Text style={styles.compactIconEmoji}>📱</Text>
                          </TouchableOpacity>

                          {/* Manage Subscription */}
                          <TouchableOpacity
                            style={styles.compactIconButton}
                            onPress={() => {
                              openAssignSubscriptionModal(shop);
                              if (!shopSubscriptions[shop.id]) {
                                loadShopSubscriptions(shop.id);
                              }
                            }}
                          >
                            <Text style={styles.compactIconEmoji}>💳</Text>
                          </TouchableOpacity>

                          {/* Toggle Users Section */}
                          <TouchableOpacity
                            style={styles.compactIconButton}
                            onPress={() => toggleShopExpansion(shop.id)}
                          >
                            <Text style={styles.compactIconEmoji}>
                              {expandedShops[shop.id] ? '👁️' : '👥'}
                            </Text>
                          </TouchableOpacity>

                          {/* Delete Shop */}
                          <TouchableOpacity
                            style={[styles.compactIconButton, styles.compactIconButtonDanger]}
                            onPress={() => handleDeleteShop(shop.id, shop.name)}
                            disabled={updatingShop === shop.id}
                          >
                            <Text style={styles.compactIconEmoji}>
                              {updatingShop === shop.id ? '⏳' : '🗑️'}
                            </Text>
                          </TouchableOpacity>
                        </View>

                        {/* Status Toggle Switch */}
                        <TouchableOpacity
                          style={[
                            styles.statusToggle,
                            shop.isLive ? styles.statusToggleOn : styles.statusToggleOff,
                          ]}
                          onPress={() =>
                            handleToggleShopStatus(shop.id, shop.name, shop.isLive)
                          }
                          disabled={updatingShop === shop.id}
                        >
                          <View
                            style={[
                              styles.statusToggleKnob,
                              shop.isLive ? styles.statusToggleKnobOn : styles.statusToggleKnobOff,
                            ]}
                          >
                            {updatingShop === shop.id ? (
                              <ActivityIndicator size="small" color={colors.white} />
                            ) : (
                              <Text style={styles.statusToggleIcon}>
                                {shop.isLive ? '🟢' : '🔴'}
                              </Text>
                            )}
                          </View>
                        </TouchableOpacity>
                      </View>
                      <Text variant="caption" color="secondary" style={styles.shopMeta}>
                        👤 {shop.owner} • 🏪 {shop.shopType}
                      </Text>
                      <Text variant="caption" color="secondary" style={styles.shopMeta}>
                        📧 {shop.email}
                      </Text>
                      <Text variant="caption" color="secondary" style={styles.shopMeta}>
                        📱 +{shop.mobileCountryCode} {shop.mobileNumber}
                      </Text>
                    </View>
                  </View>

                  {/* Subscription Information */}
                  {(() => {
                    const subscriptions = shopSubscriptions[shop.id] || [];

                    if (subscriptions.length === 0) {
                      return (
                        <View style={styles.subscriptionSection}>
                          <Text variant="caption" color="secondary" style={styles.noSubscriptionText}>
                            💳 No active subscription
                          </Text>
                        </View>
                      );
                    }

                    // Find subscription with largest expiry date
                    const latestSubscription = subscriptions.reduce((latest, current) => {
                      const latestDate = new Date(latest.planEndDate);
                      const currentDate = new Date(current.planEndDate);
                      return currentDate > latestDate ? current : latest;
                    }, subscriptions[0]);

                    // Get plan name - either from subscription or lookup from plans array
                    let planName = latestSubscription.planName?.trim();

                    console.log('🔍 [Dashboard] Plan lookup:', {
                      subscriptionPlanName: latestSubscription.planName,
                      planId: latestSubscription.planId,
                      availablePlansCount: subscriptionPlans.length,
                    });

                    if (!planName && latestSubscription.planId) {
                      const plan = subscriptionPlans.find(p => p.id === latestSubscription.planId);
                      console.log('🔍 [Dashboard] Found plan by ID:', plan);
                      planName = plan?.planName || plan?.name;
                    }

                    // Final fallback
                    if (!planName) {
                      planName = 'Unknown Plan';
                    }

                    const today = new Date();
                    const endDate = new Date(latestSubscription.planEndDate);
                    const daysLeft = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                    const isExpiringSoon = daysLeft <= 7 && daysLeft > 0;
                    const isExpired = daysLeft < 0;

                    // Format expiry date as "30th Jan 2026 mid night"
                    const day = endDate.getDate();
                    const daySuffix = day === 1 || day === 21 || day === 31 ? 'st' :
                                     day === 2 || day === 22 ? 'nd' :
                                     day === 3 || day === 23 ? 'rd' : 'th';
                    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    const formattedDate = `${day}${daySuffix} ${monthNames[endDate.getMonth()]} ${endDate.getFullYear()}`;
                    const hours = endDate.getHours();
                    const minutes = endDate.getMinutes();
                    const timeOfDay = hours === 0 && minutes === 0 ? ' mid night' :
                                     hours === 23 && minutes === 59 ? ' mid night' :
                                     ` at ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

                    return (
                      <View style={styles.subscriptionSection}>
                        <Text variant="caption" style={styles.subscriptionText}>
                          💳 <Text style={styles.subscriptionBold}>Current Plan:</Text> {planName || 'N/A'} |{' '}
                          <Text
                            style={[
                              styles.subscriptionBold,
                              isExpired ? styles.expiredText :
                              isExpiringSoon ? styles.expiringText :
                              styles.activeText
                            ]}
                          >
                            {isExpired
                              ? `Expired ${Math.abs(daysLeft)} ${Math.abs(daysLeft) === 1 ? 'day' : 'days'} ago`
                              : `${daysLeft} ${daysLeft === 1 ? 'Day' : 'Days'} Left`}
                          </Text> |{' '}
                          Expiring on {formattedDate}{timeOfDay}
                        </Text>
                      </View>
                    );
                  })()}


                  {/* Users Section (Expandable) */}
                  {expandedShops[shop.id] && (
                    <View style={styles.usersSection}>
                      <View style={styles.usersSectionHeader}>
                        <Text variant="body" bold style={styles.usersSectionTitle}>
                          👥 Shop Users
                        </Text>
                        <TouchableOpacity
                          onPress={() => loadShopUsers(shop.id)}
                          style={styles.refreshButton}
                        >
                          <Text style={styles.refreshIcon}>🔄</Text>
                        </TouchableOpacity>
                      </View>

                      {loadingUsers[shop.id] ? (
                        <View style={styles.usersLoading}>
                          <ActivityIndicator size="small" color={colors.primary} />
                          <Text variant="caption" color="secondary" style={styles.loadingText}>
                            Loading users...
                          </Text>
                        </View>
                      ) : shopUsers[shop.id]?.length > 0 ? (
                        <View style={styles.usersList}>
                          {shopUsers[shop.id].map((user) => (
                            <View key={user.id} style={styles.userCard}>
                              <View style={styles.userInfo}>
                                <Text variant="body" style={styles.userName}>
                                  {user.email || user.userId}
                                </Text>
                                <Text variant="caption" color="secondary" style={styles.userRole}>
                                  Role: {user.role} | Status: {user.status || 'ACTIVE'}
                                </Text>
                                {user.mobile && (
                                  <Text variant="caption" color="secondary" style={styles.userMobile}>
                                    📱 +{user.mobileCountryCode} {user.mobile}
                                  </Text>
                                )}
                              </View>

                              <View style={styles.userActions}>
                                {/* Reset Password Button */}
                                <TouchableOpacity
                                  onPress={() => openResetPasswordModal(user.id, user.email || user.userId)}
                                  disabled={processingAction === user.id}
                                  style={[styles.userActionButton, styles.resetPasswordButton]}
                                >
                                  <Text style={styles.userActionText}>
                                    {processingAction === user.id ? '⏳' : '🔑 Reset Password'}
                                  </Text>
                                </TouchableOpacity>

                                {/* Delete User Button (Only for STAFF) */}
                                {user.role === 'STAFF' && (
                                  <TouchableOpacity
                                    onPress={() => handleDeleteUser(user.id, user.email || user.userId, shop.id)}
                                    disabled={processingAction === user.id}
                                    style={[styles.userActionButton, styles.deleteUserButton]}
                                  >
                                    <Text style={[styles.userActionText, styles.deleteUserText]}>
                                      {processingAction === user.id ? '⏳' : '🗑️ Delete'}
                                    </Text>
                                  </TouchableOpacity>
                                )}
                              </View>
                            </View>
                          ))}
                        </View>
                      ) : (
                        <View style={styles.noUsers}>
                          <Text style={styles.noUsersIcon}>👤</Text>
                          <Text variant="caption" color="secondary" style={styles.noUsersText}>
                            No users found for this shop
                          </Text>
                        </View>
                      )}
                    </View>
                  )}
                </Card>
              ));
              })()}
            </View>
          )}
        </View>
      </ScrollView>

      {/* Reset Password Modal */}
      <Modal
        visible={resetPasswordModal.visible}
        transparent={true}
        animationType="fade"
        onRequestClose={closeResetPasswordModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text variant="h4" bold style={styles.modalTitle}>
                🔑 Reset Password
              </Text>
              <TouchableOpacity
                onPress={closeResetPasswordModal}
                style={styles.modalCloseButton}
              >
                <Text style={styles.modalCloseText}>✕</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.modalContent}>
              <Text variant="body" style={styles.modalDescription}>
                Reset password for: <Text style={styles.modalUserName}>{resetPasswordModal.userName}</Text>
              </Text>

              <View style={styles.inputGroup}>
                <Text variant="caption" style={styles.inputLabel}>New Password</Text>
                <TextInput
                  style={styles.modalInput}
                  value={newPassword}
                  onChangeText={setNewPassword}
                  placeholder="Enter new password (min 8 characters)"
                  secureTextEntry={true}
                  autoCapitalize="none"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text variant="caption" style={styles.inputLabel}>Confirm Password</Text>
                <TextInput
                  style={styles.modalInput}
                  value={confirmPassword}
                  onChangeText={setConfirmPassword}
                  placeholder="Confirm new password"
                  secureTextEntry={true}
                  autoCapitalize="none"
                />
              </View>

              <View style={styles.modalActions}>
                <TouchableOpacity
                  onPress={closeResetPasswordModal}
                  style={[styles.modalButton, styles.modalCancelButton]}
                >
                  <Text style={styles.modalCancelText}>Cancel</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={handleResetPassword}
                  disabled={processingAction === resetPasswordModal.userId}
                  style={[styles.modalButton, styles.modalConfirmButton]}
                >
                  <Text style={styles.modalConfirmText}>
                    {processingAction === resetPasswordModal.userId ? '⏳ Resetting...' : 'Reset Password'}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </Modal>


      {/* Assign Subscription Modal */}
      <Modal
        visible={showAssignSubscriptionModal}
        transparent={true}
        animationType="fade"
        onRequestClose={closeAssignSubscriptionModal}
      >
        <View style={[styles.modalOverlay, isDesktop && styles.modalOverlayDesktop]}>
          <View style={[styles.modalContainer, isDesktop && styles.modalContainerDesktop]}>
            {/* Fixed Header */}
            <View style={styles.modalHeader}>
              <Text variant="h4" bold style={styles.modalTitle}>
                💳 Manage Subscription
              </Text>
              <TouchableOpacity
                onPress={closeAssignSubscriptionModal}
                style={styles.modalCloseButton}
              >
                <Text style={styles.modalCloseText}>✕</Text>
              </TouchableOpacity>
            </View>

            {/* Scrollable Content */}
            <ScrollView
              style={styles.modalScrollView}
              contentContainerStyle={styles.modalContent}
              showsVerticalScrollIndicator={true}
            >
              {assigningToShop && (
                <Text variant="body" style={styles.modalDescription}>
                  Shop: <Text style={styles.modalUserName}>{assigningToShop.name}</Text>
                </Text>
              )}

              {/* Current Subscriptions */}
              {assigningToShop && shopSubscriptions[assigningToShop.id] && shopSubscriptions[assigningToShop.id].length > 0 && (
                <View style={styles.currentSubscriptions}>
                  <Text variant="caption" bold style={styles.inputLabel}>
                    Current Subscriptions
                  </Text>
                  {shopSubscriptions[assigningToShop.id].map((sub) => (
                    <View key={sub.id} style={styles.subscriptionItem}>
                      <View style={styles.subscriptionInfo}>
                        <Text variant="caption" bold>
                          {sub.planName || subscriptionPlans.find(p => p.id === sub.planId)?.planName || 'Unknown Plan'}
                        </Text>
                        <Text variant="caption" color="secondary">
                          {new Date(sub.planStartDate).toLocaleDateString()} to {new Date(sub.planEndDate).toLocaleDateString()}
                        </Text>
                        <Text variant="caption" style={[
                          styles.subscriptionStatus,
                          { color: sub.isActive ? colors.success : colors.error }
                        ]}>
                          {sub.isActive ? 'ACTIVE' : 'INACTIVE'}
                        </Text>
                      </View>
                      <View style={styles.subscriptionActions}>
                        <TouchableOpacity
                          onPress={() => handleRenewShopSubscription(assigningToShop.id, sub.planId, sub.id!)}
                          disabled={processingAction === sub.id}
                          style={styles.renewSubscriptionButton}
                        >
                          <Text style={styles.renewSubscriptionText}>
                            {processingAction === sub.id ? '⏳' : '🔄'}
                          </Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          onPress={() => handleDeleteShopSubscription(sub.id!, assigningToShop.id)}
                          disabled={processingAction === sub.id}
                          style={styles.removeSubscriptionButton}
                        >
                          <Text style={styles.removeSubscriptionText}>
                            {processingAction === sub.id ? '⏳' : '🗑️'}
                          </Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                  ))}
                </View>
              )}

              {/* Add New Subscription */}
              <View style={styles.addSubscriptionSection}>
                <Text variant="caption" bold style={styles.inputLabel}>
                  Add New Subscription
                </Text>

                <View style={styles.inputGroup}>
                  <Text variant="caption" style={styles.inputLabel}>Subscription Plan *</Text>
                  <View style={styles.selectWrapper}>
                    <select
                      style={styles.modalSelect}
                      value={selectedPlanId}
                      onChange={(e) => {
                        const planId = e.target.value;
                        setSelectedPlanId(planId);

                        // Auto-calculate dates based on selected plan
                        if (planId) {
                          const selectedPlan = subscriptionPlans.find(p => p.id === planId);
                          if (selectedPlan) {
                            // Set start date to today
                            const today = new Date();
                            const startDate = today.toISOString().split('T')[0];
                            setSubscriptionStartDate(startDate);

                            // Calculate end date based on subscription days
                            const endDate = new Date(today);
                            endDate.setDate(endDate.getDate() + selectedPlan.subscriptionDays);
                            setSubscriptionEndDate(endDate.toISOString().split('T')[0]);

                            console.log(`📅 [Dashboard] Auto-calculated dates for ${selectedPlan.planName}: ${startDate} to ${endDate.toISOString().split('T')[0]} (${selectedPlan.subscriptionDays} days)`);
                          }
                        }
                      }}
                    >
                      <option value="">Select a plan...</option>
                      {subscriptionPlans.filter(p => p.isActive).map((plan) => (
                        <option key={plan.id} value={plan.id}>
                          {plan.planName} ({plan.subscriptionDays} days)
                        </option>
                      ))}
                    </select>
                  </View>
                </View>

                <View style={styles.inputRow}>
                  <View style={[styles.inputGroup, styles.inputHalf]}>
                    <Text variant="caption" style={styles.inputLabel}>Start Date * (Auto-calculated)</Text>
                    <TextInput
                      style={[styles.modalInput, styles.disabledInput]}
                      value={subscriptionStartDate}
                      editable={false}
                      placeholder="YYYY-MM-DD"
                    />
                    <Text variant="caption" color="secondary" style={{ marginTop: 4 }}>
                      Starts today
                    </Text>
                  </View>

                  <View style={[styles.inputGroup, styles.inputHalf]}>
                    <Text variant="caption" style={styles.inputLabel}>End Date * (Auto-calculated)</Text>
                    <TextInput
                      style={[styles.modalInput, styles.disabledInput]}
                      value={subscriptionEndDate}
                      editable={false}
                      placeholder="YYYY-MM-DD"
                    />
                    <Text variant="caption" color="secondary" style={{ marginTop: 4 }}>
                      Based on plan duration
                    </Text>
                  </View>
                </View>

                <View style={styles.inputGroup}>
                  <Text variant="caption" style={styles.inputLabel}>Partner ID (Optional)</Text>
                  <TextInput
                    style={styles.modalInput}
                    value={subscriptionPartnerId}
                    onChangeText={setSubscriptionPartnerId}
                    placeholder="e.g., E00000000001"
                  />
                  <Text variant="caption" color="secondary" style={{ marginTop: 4 }}>
                    Partner who created this subscription
                  </Text>
                </View>

                <View style={styles.checkboxGroup}>
                  <TouchableOpacity
                    onPress={() => setAutoRenew(!autoRenew)}
                    style={styles.checkbox}
                  >
                    <View style={[styles.checkboxBox, autoRenew && styles.checkboxBoxChecked]}>
                      {autoRenew && <Text style={styles.checkboxCheck}>✓</Text>}
                    </View>
                    <Text variant="body" style={styles.checkboxLabel}>
                      Auto Renew
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            </ScrollView>

            {/* Fixed Footer */}
            <View style={styles.modalActions}>
              <TouchableOpacity
                onPress={closeAssignSubscriptionModal}
                style={[styles.modalButton, styles.modalCancelButton]}
              >
                <Text style={styles.modalCancelText}>Close</Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={handleAssignSubscription}
                disabled={processingAction === 'assigning-subscription'}
                style={[styles.modalButton, styles.modalConfirmButton]}
              >
                <Text style={styles.modalConfirmText}>
                  {processingAction === 'assigning-subscription' ? '⏳ Assigning...' : 'Assign Subscription'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* QR Code Modal - Printable Format */}
      <Modal
        visible={showQRModal}
        transparent={true}
        animationType="fade"
        onRequestClose={handleCloseQRModal}
      >
        <View style={styles.qrModalOverlay}>
          <View style={styles.qrModalContent}>
            {/* Header */}
            <View style={styles.qrModalHeader}>
              <Text variant="h3" bold style={styles.qrModalTitle}>
                🎟️ Shop QR Code
              </Text>
              <TouchableOpacity onPress={handleCloseQRModal}>
                <Text style={styles.qrModalCloseButton}>✕</Text>
              </TouchableOpacity>
            </View>

            {/* Printable QR Code Section */}
            <ScrollView
              style={styles.qrModalScroll}
              contentContainerStyle={styles.qrModalScrollContent}
              showsVerticalScrollIndicator={true}
            >
              {selectedShopForQR?.qrCode ? (
                <View style={styles.printableQRContainer}>
                  {/* Printable QR Card with Shop Image Background */}
                  <View style={styles.printableQRCard}>
                    {/* Background Image with 30% opacity overlay */}
                    {selectedShopForQR.shopImage && (
                      <View style={styles.qrBackgroundContainer}>
                        <Image
                          source={{ uri: selectedShopForQR.shopImage }}
                          style={styles.qrBackgroundImage}
                          resizeMode="cover"
                        />
                        <View style={styles.qrBackgroundOverlay} />
                      </View>
                    )}

                    {/* Content */}
                    <View style={styles.qrContentContainer}>
                      {/* Shop Name on Top */}
                      <Text style={styles.qrPrintShopName}>
                        {selectedShopForQR.name}
                      </Text>

                      {/* Instructional Text */}
                      <Text style={styles.qrPrintInstructions}>
                        Find Our Products and Prices by Scanning Below QR
                      </Text>

                      {/* Spacing */}
                      <View style={styles.qrSpacing} />

                      {/* QR Code in Center */}
                      <View style={styles.qrCodeWrapper}>
                        <View style={styles.qrCodeBox}>
                          <Image
                            source={{ uri: selectedShopForQR.qrCode }}
                            style={styles.qrCodeImagePrintable}
                            resizeMode="contain"
                          />
                        </View>
                      </View>

                      {/* Footer */}
                      <Text style={styles.qrPrintFooter}>
                        Powered by Bynaryx Labs™
                      </Text>
                    </View>
                  </View>

                  {/* Info Note */}
                  <Text variant="caption" style={styles.qrPrintNote}>
                    💡 This QR code is optimized for printing and can be displayed in your shop like payment QR codes
                  </Text>
                </View>
              ) : (
                <View style={styles.qrCodePlaceholder}>
                  <Text variant="body" color="secondary">
                    QR Code not available
                  </Text>
                </View>
              )}

              {/* Customer View URL */}
              {selectedShopForQR?.id && (
                <View style={styles.qrUrlContainer}>
                  <Text variant="caption" bold style={styles.qrUrlLabel}>
                    Customer View URL:
                  </Text>
                  <Text variant="caption" color="secondary" style={styles.qrUrlText}>
                    {getCustomerShopMenuUrl(selectedShopForQR.id)}
                  </Text>
                </View>
              )}
            </ScrollView>

            {/* Actions */}
            <View style={styles.qrModalActions}>
              {selectedShopForQR?.qrCode && (
                <TouchableOpacity
                  style={[styles.qrModalButton, styles.qrDownloadButton]}
                  onPress={handleDownloadQRCode}
                >
                  <Text style={styles.qrDownloadButtonText}>
                    ⬇️ Download Printable QR
                  </Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity
                style={[styles.qrModalButton, styles.qrCloseButtonBottom]}
                onPress={handleCloseQRModal}
              >
                <Text style={styles.qrCloseButtonText}>
                  Close
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.white,
  },
  loadingText: {
    marginTop: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: colors.primary,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray2,
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 24,
    color: colors.white,
  },
  headerSubtitle: {
    color: colors.white,
    opacity: 0.8,
  },
  menuButton: {
    flexDirection: 'column',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 8,
  },
  menuIcon: {
    fontSize: 24,
    color: colors.white,
    marginBottom: 2,
  },
  menuText: {
    color: colors.white,
    fontSize: 10,
    fontWeight: '600',
  },
  menuOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    padding: 0,
  },
  menuContainer: {
    backgroundColor: colors.white,
    borderRadius: 12,
    marginTop: 70,
    marginRight: 16,
    minWidth: 280,
    maxWidth: 340,
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.3)',
    elevation: 10,
  },
  menuHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray3,
  },
  closeButton: {
    fontSize: 24,
    color: colors.gray6,
    fontWeight: 'bold',
    padding: 4,
  },
  menuContent: {
    maxHeight: 500,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray2,
  },
  menuItemIcon: {
    fontSize: 20,
    marginRight: 12,
    width: 24,
    textAlign: 'center',
  },
  menuItemText: {
    fontSize: 16,
    color: colors.text,
    fontWeight: '500',
  },
  menuDivider: {
    height: 1,
    backgroundColor: colors.gray3,
    marginVertical: 8,
  },
  menuItemDanger: {
    color: colors.error,
  },
  logoutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 8,
  },
  logoutIcon: {
    fontSize: 20,
    marginRight: 4,
  },
  logoutText: {
    color: colors.white,
    fontWeight: '600',
  },
  container: {
    flex: 1,
    backgroundColor: colors.gray1,
  },
  contentContainer: {
    paddingVertical: 16,
  },
  sectionTitle: {
    fontSize: 18,
    marginBottom: 12,
    paddingHorizontal: 16,
  },
  statsSection: {
    marginBottom: 24,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 16,
    gap: 12,
  },
  statCard: {
    flex: 1,
    minWidth: 100,
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 8,
  },
  statCardSuccess: {
    borderLeftWidth: 4,
    borderLeftColor: colors.success,
  },
  statCardError: {
    borderLeftWidth: 4,
    borderLeftColor: colors.error,
  },
  statIcon: {
    fontSize: 32,
    marginBottom: 8,
  },
  statValue: {
    fontSize: 32,
    marginBottom: 4,
  },
  statLabel: {
    textAlign: 'center',
  },
  shopsSection: {
    marginBottom: 24,
  },
  shopsList: {
    paddingHorizontal: 16,
  },
  shopCard: {
    marginBottom: 12,
  },
  shopHeader: {
    marginBottom: 12,
  },
  shopInfo: {
    flex: 1,
  },
  shopTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
    flexWrap: 'nowrap',
  },
  compactActionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginLeft: 4,
    marginRight: 4,
    flexShrink: 0,
  },
  shopName: {
    fontSize: 16,
    flex: 1,
    marginRight: 8,
    minWidth: 0,
    flexShrink: 1,
  },
  shopMeta: {
    fontSize: 12,
    marginBottom: 4,
    lineHeight: 18,
  },
  // Status Toggle Switch
  statusToggle: {
    width: 52,
    height: 28,
    borderRadius: 14,
    padding: 2,
    justifyContent: 'center',
    flexShrink: 0,
  },
  statusToggleOn: {
    backgroundColor: colors.success,
  },
  statusToggleOff: {
    backgroundColor: colors.gray4,
  },
  statusToggleKnob: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.white,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 3,
    elevation: 3,
  },
  statusToggleKnobOn: {
    alignSelf: 'flex-end',
  },
  statusToggleKnobOff: {
    alignSelf: 'flex-start',
  },
  statusToggleIcon: {
    fontSize: 12,
  },
  // Subscription Section
  subscriptionSection: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: colors.gray2,
    borderRadius: 8,
    marginBottom: 12,
  },
  subscriptionText: {
    fontSize: 12,
    lineHeight: 18,
  },
  subscriptionBold: {
    fontWeight: '600',
  },
  noSubscriptionText: {
    fontSize: 12,
    fontStyle: 'italic',
  },
  activeText: {
    color: colors.success,
  },
  expiringText: {
    color: '#FF9500',
  },
  expiredText: {
    color: colors.error,
  },
  // Shop Actions - Mobile First Icon Buttons
  shopActions: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 12,
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  // Compact Icon-Only Buttons (New Design)
  compactIconButton: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: colors.gray4,
    backgroundColor: colors.white,
  },
  compactIconEmoji: {
    fontSize: 16,
  },
  compactIconButtonDanger: {
    borderColor: colors.error + '40',
    backgroundColor: colors.error + '08',
  },
  // Old button styles (kept for backwards compatibility)
  iconButton: {
    flex: 1,
    minWidth: 70,
    maxWidth: 110,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    paddingHorizontal: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.gray4,
    backgroundColor: colors.white,
  },
  iconButtonEmoji: {
    fontSize: 24,
    marginBottom: 4,
  },
  iconButtonLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.gray7,
    textAlign: 'center',
  },
  iconButtonDanger: {
    borderColor: colors.error + '30',
    backgroundColor: colors.error + '05',
  },
  // Toggle Button for Online/Offline (Removed from actions - only in header)
  toggleButton: {
    minWidth: 48,
    maxWidth: 48,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 24,
    borderWidth: 2,
    borderColor: colors.error + '60',
    backgroundColor: colors.error + '10',
  },
  toggleButtonActive: {
    borderColor: colors.success + '60',
    backgroundColor: colors.success + '10',
  },
  toggleEmoji: {
    fontSize: 24,
  },
  iconButtonLabelDanger: {
    color: colors.error,
  },
  actionButton: {
    flex: 1,
    paddingVertical: 10,
  },
  deleteButton: {
    borderColor: colors.error,
  },
  emptyCard: {
    padding: 48,
    alignItems: 'center',
    marginHorizontal: 16,
  },
  emptyIcon: {
    fontSize: 64,
    marginBottom: 16,
  },
  emptyTitle: {
    fontSize: 18,
    marginBottom: 8,
  },
  emptyText: {
    textAlign: 'center',
  },
  // Subscription Info Styles
  subscriptionInfo: {
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray3,
  },
  subscriptionHeader: {
    marginBottom: 8,
  },
  subscriptionLabel: {
    fontSize: 13,
    color: colors.primary,
  },
  subscriptionDetails: {
    backgroundColor: colors.gray2,
    borderRadius: 8,
    padding: 12,
    gap: 6,
  },
  subscriptionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  subscriptionKey: {
    fontSize: 12,
    color: colors.text,
    opacity: 0.7,
  },
  subscriptionValue: {
    fontSize: 12,
    color: colors.text,
  },
  noSubscription: {
    backgroundColor: colors.gray2,
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
  },
  // User Management Styles
  usersSection: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: colors.gray3,
  },
  usersSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  usersSectionTitle: {
    fontSize: 16,
    color: colors.primary,
  },
  refreshButton: {
    padding: 4,
  },
  refreshIcon: {
    fontSize: 16,
  },
  usersLoading: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 20,
    gap: 8,
  },
  usersList: {
    gap: 8,
  },
  userCard: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colors.gray2,
    borderRadius: 8,
    padding: 12,
  },
  userInfo: {
    flex: 1,
  },
  userName: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 2,
  },
  userRole: {
    fontSize: 12,
    marginBottom: 2,
  },
  userMobile: {
    fontSize: 12,
  },
  userActions: {
    flexDirection: 'row',
    gap: 8,
  },
  userActionButton: {
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.primary,
    backgroundColor: 'transparent',
  },
  resetPasswordButton: {
    borderColor: colors.primary,
  },
  deleteUserButton: {
    borderColor: colors.error,
  },
  userActionText: {
    fontSize: 12,
    color: colors.primary,
    fontWeight: '600',
  },
  deleteUserText: {
    color: colors.error,
  },
  noUsers: {
    alignItems: 'center',
    paddingVertical: 20,
  },
  noUsersIcon: {
    fontSize: 32,
    marginBottom: 8,
  },
  noUsersText: {
    textAlign: 'center',
  },
  // Modal Styles - Mobile First Design
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end', // Mobile: bottom sheet
    alignItems: 'center',
    padding: 0,
  },
  modalOverlayDesktop: {
    justifyContent: 'center', // Desktop: center modal
    padding: 20,
  },
  modalContainer: {
    backgroundColor: colors.white,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    width: '100%',
    maxWidth: 600, // Desktop: max width
    maxHeight: '95%', // Allow more screen space
    minHeight: '50%',
    display: 'flex',
    flexDirection: 'column',
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
    elevation: 10,
  },
  modalContainerDesktop: {
    borderRadius: 12, // Desktop: rounded all corners
    maxHeight: '90vh',
    minHeight: 'auto',
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    paddingTop: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray3,
    backgroundColor: colors.white,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
  },
  modalTitle: {
    fontSize: 18,
    color: colors.primary,
  },
  modalCloseButton: {
    padding: 8,
    marginRight: -8,
  },
  modalCloseText: {
    fontSize: 24,
    color: colors.gray6,
    fontWeight: 'bold',
    lineHeight: 24,
  },
  modalScrollView: {
    flex: 1, // Take available space between header and footer
    backgroundColor: '#f9f9f9',
  },
  modalContent: {
    padding: 20,
    paddingBottom: 24,
  },
  modalDescription: {
    marginBottom: 20,
    fontSize: 14,
    lineHeight: 20,
  },
  modalUserName: {
    fontWeight: 'bold',
    color: colors.primary,
  },
  inputGroup: {
    marginBottom: 20, // More spacing on mobile
  },
  inputLabel: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 8,
    color: colors.gray7,
  },
  modalInput: {
    borderWidth: 1,
    borderColor: colors.gray4,
    borderRadius: 8,
    padding: 12,
    paddingVertical: 14, // Larger tap area
    fontSize: 16, // Prevent zoom on mobile
    backgroundColor: colors.white,
    minHeight: 48, // Touch target
  },
  disabledInput: {
    backgroundColor: colors.gray2,
    borderColor: colors.gray3,
    color: colors.gray6,
    opacity: 0.7,
  },
  disabledCheckbox: {
    opacity: 0.5,
  },
  infoBox: {
    backgroundColor: '#E3F2FD',
    borderLeftWidth: 4,
    borderLeftColor: '#2196F3',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    padding: 20,
    paddingBottom: 24,
    borderTopWidth: 1,
    borderTopColor: colors.gray3,
    backgroundColor: colors.white,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48, // Touch target
  },
  modalCancelButton: {
    backgroundColor: colors.gray3,
    borderWidth: 1,
    borderColor: colors.gray4,
  },
  modalConfirmButton: {
    backgroundColor: colors.primary,
  },
  modalCancelText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray7,
  },
  modalConfirmText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
  },
  // Subscription Plan Management Styles
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    marginBottom: 12,
    flexWrap: 'wrap',
    gap: 8,
  },
  addButton: {
    backgroundColor: colors.primary,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    minWidth: 90,
    alignItems: 'center',
  },
  addButtonText: {
    color: colors.white,
    fontSize: 13,
    fontWeight: '600',
  },
  plansList: {
    paddingHorizontal: 16,
  },
  planCard: {
    marginBottom: 12,
  },
  planHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  planInfo: {
    flex: 1,
    marginRight: 12,
  },
  planName: {
    fontSize: 16,
    marginBottom: 4,
  },
  planDescription: {
    fontSize: 12,
    marginBottom: 8,
  },
  planDetails: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginBottom: 8,
  },
  planPrice: {
    fontSize: 18,
    color: colors.primary,
  },
  planCycle: {
    fontSize: 12,
    marginLeft: 4,
  },
  planLimits: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 8,
  },
  planLimit: {
    fontSize: 12,
    color: colors.gray6,
  },
  planFeatures: {
    marginTop: 8,
  },
  planFeature: {
    fontSize: 12,
    color: colors.success,
    marginBottom: 2,
  },
  planActions: {
    flexDirection: 'row',
    gap: 8,
  },
  wideModal: {
    maxWidth: 600,
  },
  modalScrollContent: {
    flexGrow: 1,
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  inputRow: {
    flexDirection: 'row',
    gap: 12,
  },
  inputHalf: {
    flex: 1,
  },
  selectWrapper: {
    borderWidth: 1,
    borderColor: colors.gray4,
    borderRadius: 8,
    backgroundColor: colors.white,
  },
  modalSelect: {
    width: '100%',
    padding: 12,
    fontSize: 14,
    border: 'none',
    backgroundColor: 'transparent',
  } as any,
  featureInputRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 8,
  },
  featureInput: {
    flex: 1,
  },
  addFeatureButton: {
    backgroundColor: colors.primary,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    justifyContent: 'center',
  },
  addFeatureText: {
    color: colors.white,
    fontSize: 14,
    fontWeight: '600',
  },
  featuresList: {
    marginTop: 8,
  },
  featureItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colors.gray2,
    padding: 8,
    borderRadius: 6,
    marginBottom: 4,
  },
  featureText: {
    fontSize: 13,
    color: colors.gray8,
  },
  removeFeatureButton: {
    padding: 4,
  },
  removeFeatureText: {
    fontSize: 16,
    color: colors.error,
    fontWeight: 'bold',
  },
  checkboxGroup: {
    marginBottom: 20,
  },
  checkbox: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 44, // Touch target
  },
  checkboxBox: {
    width: 24,
    height: 24,
    borderWidth: 2,
    borderColor: colors.gray4,
    borderRadius: 4,
    marginRight: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxBoxChecked: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  checkboxCheck: {
    color: colors.white,
    fontSize: 16,
    fontWeight: 'bold',
  },
  checkboxLabel: {
    fontSize: 16, // Larger on mobile
  },
  currentSubscriptions: {
    marginBottom: 20,
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray3,
  },
  subscriptionItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colors.gray2,
    padding: 12,
    borderRadius: 8,
    marginTop: 8,
  },
  subscriptionInfoRow: {
    flex: 1,
    marginRight: 12,
  },
  subscriptionActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  subscriptionStatus: {
    fontSize: 11,
    fontWeight: 'bold',
    marginTop: 4,
  },
  renewSubscriptionButton: {
    padding: 8,
    backgroundColor: colors.primary,
    borderRadius: 6,
    minWidth: 40,
    minHeight: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  renewSubscriptionText: {
    fontSize: 18,
  },
  removeSubscriptionButton: {
    padding: 8,
    backgroundColor: colors.error,
    borderRadius: 6,
    minWidth: 40,
    minHeight: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  removeSubscriptionText: {
    fontSize: 18,
  },
  addSubscriptionSection: {
    marginTop: 16,
  },
  // Shop Type Filter Styles
  shopTypeFilterContainer: {
    marginHorizontal: 16,
    marginBottom: 12,
  },
  filterLabel: {
    marginBottom: 8,
    fontSize: 12,
    fontWeight: '500',
  },
  shopTypeFilterScroll: {
    flexGrow: 0,
  },
  shopTypeFilterContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 4,
  },
  shopTypeFilterButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: colors.white,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.gray3,
  },
  shopTypeFilterButtonActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  shopTypeFilterText: {
    fontSize: 12,
    color: colors.gray6,
    fontWeight: '500',
  },
  shopTypeFilterTextActive: {
    color: colors.white,
    fontWeight: '600',
  },
  // Status Filter Styles (Online/Offline)
  statusFilterContainer: {
    marginBottom: 12,
    paddingHorizontal: 16,
  },
  statusFilterButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    paddingVertical: 4,
  },
  statusFilterButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: colors.white,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.gray3,
  },
  statusFilterButtonActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  statusFilterButtonActiveOnline: {
    backgroundColor: colors.success,
    borderColor: colors.success,
  },
  statusFilterButtonActiveOffline: {
    backgroundColor: colors.error,
    borderColor: colors.error,
  },
  statusFilterText: {
    fontSize: 12,
    color: colors.gray6,
    fontWeight: '500',
  },
  statusFilterTextActive: {
    color: colors.white,
    fontWeight: '600',
  },
  statusFilterTextActiveOnline: {
    color: colors.white,
    fontWeight: '600',
  },
  statusFilterTextActiveOffline: {
    color: colors.white,
    fontWeight: '600',
  },
  clearFiltersButton: {
    marginTop: 12,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: colors.primary,
    borderRadius: 6,
    alignSelf: 'center',
  },
  clearFiltersText: {
    color: colors.white,
    fontWeight: '600',
    fontSize: 14,
  },
  searchContainer: {
    position: 'relative',
    marginBottom: 16,
    marginHorizontal: 16,
  },
  searchInput: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.gray3,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 14,
    color: colors.dark,
    paddingRight: 40,
  },
  clearSearchButton: {
    position: 'absolute',
    right: 12,
    top: 12,
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.gray3,
    borderRadius: 12,
  },
  clearSearchText: {
    color: colors.gray6,
    fontSize: 14,
    fontWeight: 'bold',
  },
  // Executive Reports Styles
  reportsSection: {
    marginBottom: 24,
    paddingHorizontal: 16,
  },
  reportsSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: colors.white,
    borderRadius: 8,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: colors.gray3,
  },
  toggleIcon: {
    fontSize: 18,
    color: colors.primary,
    fontWeight: 'bold',
  },
  reportsContent: {
    backgroundColor: colors.white,
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
    borderColor: colors.gray3,
  },
  reportsLoading: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  reportsError: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chartsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 16,
  },
  chartCard: {
    flex: 1,
    minWidth: 280,
    maxWidth: '100%',
    padding: 16,
    backgroundColor: colors.gray1,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.gray3,
  },
  chartCardWide: {
    minWidth: '100%',
  },
  chartTitle: {
    fontSize: 14,
    marginBottom: 12,
    textAlign: 'center',
    color: colors.dark,
  },
  chartLegend: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 16,
    marginTop: 12,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendColor: {
    width: 16,
    height: 16,
    borderRadius: 4,
  },
  chartNote: {
    textAlign: 'center',
    marginTop: 8,
    fontSize: 11,
  },
  summaryStats: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 16,
  },
  summaryCard: {
    flex: 1,
    minWidth: 100,
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 12,
    backgroundColor: colors.gray1,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.gray3,
  },
  // QR Code Modal Styles
  qrModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  qrModalContent: {
    backgroundColor: colors.white,
    borderRadius: 16,
    width: '100%',
    maxWidth: 600,
    maxHeight: '90%',
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.3)',
    elevation: 10,
  },
  qrModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray3,
  },
  qrModalTitle: {
    fontSize: 20,
    color: colors.text,
  },
  qrModalCloseButton: {
    fontSize: 28,
    color: colors.gray6,
    fontWeight: 'bold',
    paddingHorizontal: 8,
  },
  qrModalScroll: {
    maxHeight: 600,
  },
  qrModalScrollContent: {
    padding: 20,
  },
  // Printable QR Card Styles
  printableQRContainer: {
    marginBottom: 20,
  },
  printableQRCard: {
    position: 'relative',
    width: '100%',
    aspectRatio: 0.7, // Portrait format (700:1000) like payment QR codes
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: colors.white,
    borderWidth: 2,
    borderColor: colors.gray4,
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
    elevation: 3,
  },
  qrBackgroundContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  qrBackgroundImage: {
    width: '100%',
    height: '100%',
    position: 'absolute',
  },
  qrBackgroundOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.7)', // 30% shop image, 70% white overlay
  },
  qrContentContainer: {
    position: 'relative',
    flex: 1,
    padding: 24,
    paddingTop: 32,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  qrPrintShopName: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#1a1a1a',
    textAlign: 'center',
    marginBottom: 16,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  qrPrintInstructions: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333333',
    textAlign: 'center',
    marginBottom: 12,
    lineHeight: 24,
  },
  qrSpacing: {
    height: 40, // Two line spacing
  },
  qrCodeWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },
  qrCodeBox: {
    backgroundColor: colors.white,
    padding: 16,
    borderRadius: 12,
    borderWidth: 3,
    borderColor: '#000000',
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
    elevation: 5,
  },
  qrCodeImagePrintable: {
    width: 280,
    height: 280,
  },
  qrPrintFooter: {
    fontSize: 12,
    color: '#666666',
    textAlign: 'center',
    marginTop: 20,
    fontWeight: '500',
  },
  qrPrintNote: {
    textAlign: 'center',
    fontSize: 12,
    color: colors.gray6,
    marginTop: 12,
    lineHeight: 18,
    fontStyle: 'italic',
  },
  // Old styles for fallback
  qrCodeContainer: {
    alignItems: 'center',
    marginBottom: 20,
  },
  qrCodeImage: {
    width: 300,
    height: 300,
    marginBottom: 12,
  },
  qrCodeCaption: {
    textAlign: 'center',
    fontSize: 14,
    color: colors.gray6,
  },
  qrCodePlaceholder: {
    height: 300,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.gray2,
    borderRadius: 8,
    marginBottom: 20,
  },
  qrUrlContainer: {
    backgroundColor: colors.gray1,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.gray3,
  },
  qrUrlLabel: {
    marginBottom: 6,
    fontSize: 12,
    color: colors.gray7,
  },
  qrUrlText: {
    fontSize: 11,
    lineHeight: 16,
    wordBreak: 'break-all',
  },
  qrModalActions: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: colors.gray3,
    gap: 10,
  },
  qrModalButton: {
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
  },
  qrDownloadButton: {
    backgroundColor: colors.primary,
  },
  qrDownloadButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
  },
  qrCloseButtonBottom: {
    backgroundColor: colors.gray3,
    borderWidth: 1,
    borderColor: colors.gray4,
  },
  qrCloseButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.gray7,
  },
});



